// ABOUTME: Complete referral submission flow with enhanced validation, error handling, and schema resilience
// ABOUTME: Multi-step form for selecting roles and submitting candidate referrals with fallback for schema cache issues

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Check, User, Briefcase, MessageSquare, Search, MapPin, Calendar, AlertCircle, FileText, CheckCircle, DollarSign, Clock, RefreshCw, AlertTriangle, ChevronDown, ChevronUp, ArrowLeft, Building2, Edit2, Save, X } from 'lucide-react';
import { FileUpload } from './FileUpload';
import { useJobRoles } from '@/hooks/useJobRoles';
import { useReferrals } from '@/hooks/useReferrals';
import { useAuth } from '@/hooks/useAuth';
import { sanitizeName, sanitizeText, validateEmail, validatePhoneNumber } from '@/utils/validation';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from '@/hooks/use-toast';

// Enhanced validation schema with optional phone for schema resilience
const candidateSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters").max(50, "Name must be less than 50 characters").regex(/^[a-zA-Z\s\-'\.]+$/, "Name can only contain letters, spaces, hyphens, apostrophes, and periods"),
  email: z.string().email("Please enter a valid email address").min(5, "Email must be at least 5 characters").max(100, "Email must be less than 100 characters"),
  phone: z.string().min(1, "Phone number is required").refine(val => validatePhoneNumber(val), "Please enter a valid phone number").optional(),
  // Make phone optional to handle schema cache issues
  linkedin: z.string().min(1, "LinkedIn profile is required"),
  relationship: z.string().min(1, "Please select your relationship"),
  notes: z.string().max(1000, "Notes must be less than 1000 characters").optional(),
  resumeUrl: z.string().url("Please enter a valid URL").optional().or(z.literal(''))
});
type CandidateFormData = z.infer<typeof candidateSchema>;
interface ReferralSubmissionFlowProps {
  onViewChange?: (view: string) => void;
}
const ReferralSubmissionFlow = ({
  onViewChange
}: ReferralSubmissionFlowProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedRole, setSelectedRole] = useState<any>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [schemaWarning, setSchemaWarning] = useState<string | null>(null);
  const [isFullDescriptionExpanded, setIsFullDescriptionExpanded] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [tempFormData, setTempFormData] = useState<Partial<CandidateFormData>>({});
  const {
    user
  } = useAuth();
  const {
    jobRoles,
    isLoading: rolesLoading
  } = useJobRoles();
  const {
    createReferral,
    isCreating,
    error: referralError,
    schemaValidated
  } = useReferrals();
  const form = useForm<CandidateFormData>({
    resolver: zodResolver(candidateSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      linkedin: '',
      relationship: '',
      notes: '',
      resumeUrl: ''
    },
    mode: 'onChange' // Enable real-time validation
  });
  const relationships = ["Former Colleague", "Current Colleague", "Manager/Report", "Mentor/Mentee", "Alumni Network", "Professional Contact", "Friend", "Family", "LinkedIn Connection", "Other"];

  // Show schema warning if validation failed
  React.useEffect(() => {
    if (schemaValidated === false) {
      setSchemaWarning("Database connection issue detected. Phone numbers may not be saved, but referrals will still work.");
    } else if (schemaValidated === true && schemaWarning) {
      setSchemaWarning(null);
    }
  }, [schemaValidated, schemaWarning]);

  // Extract unique departments from jobRoles, filtering out empty values
  const departments = ['all', ...Array.from(new Set(jobRoles?.map(role => role.department).filter(dept => dept && dept.trim() !== '') || []))];

  // Enhanced filter logic with both search term and department filter
  const filteredRoles = jobRoles?.filter(role => {
    const matchesSearch = role.title.toLowerCase().includes(searchTerm.toLowerCase()) || role.department.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDepartment = departmentFilter === 'all' || role.department === departmentFilter;
    const isActive = role.is_active === true; // Only show active roles for referrals
    return matchesSearch && matchesDepartment && isActive;
  }) || [];
  const getStepProgress = () => {
    if (isSubmitted) return 100;
    return currentStep / 4 * 100;
  };

  // Enhanced authentication check
  const checkAuthentication = () => {
    if (!user?.id) {
      toast({
        title: "Authentication Required",
        description: "Please log in to submit referrals.",
        variant: "destructive"
      });
      return false;
    }
    return true;
  };
  const handleRoleSelect = (role: any) => {
    if (!checkAuthentication()) return;
    setSelectedRole(role);
    setCurrentStep(2);
    setSubmissionError(null);
  };
  const handleCandidateSubmit = async (data: CandidateFormData) => {
    try {
      if (!checkAuthentication()) return;

      // If schema validation failed, make phone optional
      if (schemaValidated === false) {
        const modifiedSchema = candidateSchema.extend({
          phone: z.string().optional() // Make phone truly optional if schema has issues
        });
        const result = modifiedSchema.safeParse(data);
        if (!result.success) {
          const firstError = Object.values(result.error.formErrors.fieldErrors)[0]?.[0];
          toast({
            title: "Validation Error",
            description: firstError || "Please fix the form errors before continuing.",
            variant: "destructive"
          });
          return;
        }
      } else {
        // Trigger comprehensive validation
        const isValid = await form.trigger();
        if (!isValid) {
          const errors = form.formState.errors;
          const firstError = Object.values(errors)[0];
          toast({
            title: "Validation Error",
            description: firstError?.message || "Please fix the form errors before continuing.",
            variant: "destructive"
          });
          return;
        }

        // Additional validation for email and phone (only if schema is valid)
        if (!validateEmail(data.email)) {
          form.setError('email', {
            message: 'Please enter a valid email address'
          });
          return;
        }
        if (data.phone && !validatePhoneNumber(data.phone)) {
          form.setError('phone', {
            message: 'Please enter a valid phone number'
          });
          return;
        }
      }
      setCurrentStep(4);
      setSubmissionError(null);
    } catch (error) {
      console.error('Validation error:', error);
      toast({
        title: "Error",
        description: "Failed to validate form data. Please try again.",
        variant: "destructive"
      });
    }
  };
  const handleFinalSubmit = async (data: CandidateFormData) => {
    if (!selectedRole) {
      setSubmissionError("No role selected. Please go back and select a role.");
      return;
    }
    if (!checkAuthentication()) return;
    try {
      setSubmissionError(null);

      // Validate and sanitize all data before submission
      const sanitizedName = sanitizeName(data.name);
      const sanitizedNotes = data.notes ? sanitizeText(data.notes) : null;
      if (!sanitizedName || sanitizedName.length < 2) {
        throw new Error("Invalid name after sanitization");
      }
      if (!validateEmail(data.email)) {
        throw new Error("Invalid email format");
      }

      // Only validate phone if schema is valid and phone is provided
      if (schemaValidated !== false && data.phone && !validatePhoneNumber(data.phone)) {
        throw new Error("Invalid phone number format");
      }

      // Prepare referral data with schema-aware phone handling
      const referralData = {
        candidate_name: sanitizedName,
        candidate_email: data.email.toLowerCase().trim(),
        candidate_linkedin: data.linkedin.trim(),
        candidate_resume_url: data.resumeUrl || null,
        relationship: data.relationship,
        notes: sanitizedNotes,
        job_role_id: selectedRole.id,
        job_role: selectedRole.title,
        // Save the actual job role title to the new column
        // Only include phone if schema validation passed
        ...(schemaValidated !== false && data.phone && {
          candidate_phone: data.phone.trim()
        })
      };
      console.log('Submitting referral data:', referralData);
      await createReferral(referralData);
      setIsSubmitted(true);
    } catch (error: any) {
      console.error('Referral submission error:', error);
      let errorMessage = error?.message || "An unexpected error occurred. Please try again.";

      // Handle specific error types with user-friendly messages
      if (errorMessage.includes('authentication') || errorMessage.includes('not authenticated')) {
        errorMessage = "Your session has expired. Please refresh the page and log in again.";
      } else if (errorMessage.includes('duplicate') || errorMessage.includes('already')) {
        errorMessage = "You have already referred this candidate for this role.";
      } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
        errorMessage = "Network error. Please check your connection and try again.";
      } else if (errorMessage.includes('schema cache') || errorMessage.includes('candidate_phone')) {
        errorMessage = "Database connection issue. Your referral was submitted but phone number may not be saved.";
      }
      setSubmissionError(errorMessage);
    }
  };
  const handleRetrySubmission = async () => {
    setIsRetrying(true);
    try {
      const formData = form.getValues();
      await handleFinalSubmit(formData);
    } finally {
      setIsRetrying(false);
    }
  };
  const handleFileUpload = (url: string, fileName: string) => {
    form.setValue('resumeUrl', url);
    toast({
      title: "File Uploaded",
      description: `${fileName} has been uploaded successfully.`
    });
  };
  const handleClearFilters = () => {
    setSearchTerm('');
    setDepartmentFilter('all');
  };
  const resetFormAndState = () => {
    setCurrentStep(1);
    setSelectedRole(null);
    setIsSubmitted(false);
    setSearchTerm('');
    setDepartmentFilter('all');
    setSubmissionError(null);
    setIsRetrying(false);
    setSchemaWarning(null);
    form.reset();
  };
  const handleSubmitAnotherReferral = () => {
    if (onViewChange) {
      resetFormAndState();
      onViewChange('submit');
    } else {
      window.location.reload();
    }
  };

  // Helper function to parse requirements string into array
  const parseRequirements = (requirements: string | null | undefined): string[] => {
    if (!requirements || typeof requirements !== 'string') {
      return [];
    }
    return requirements.split('\n').map(req => req.trim()).filter(req => req.length > 0);
  };

  // Helper function to parse tools string into array
  const parseTools = (tools: string | string[] | null | undefined): string[] => {
    if (!tools) return [];
    if (Array.isArray(tools)) return tools;
    if (typeof tools === 'string') {
      // Split by common separators and clean up
      return tools.split(/[\n\r;,]/).map(tool => tool.trim()).filter(tool => tool.length > 0).map(tool => tool.replace(/^\d+\)\s*/, '')); // Remove leading numbers like "1) "
    }
    return [];
  };

  // Success state
  if (isSubmitted) {
    return <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <Card className="w-full max-w-2xl rounded-xl shadow-lg">
          <CardContent className="p-12 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Referral Submitted Successfully!</h2>
            
            <p className="text-gray-600 mb-8">
              Your referral for <strong>{form.getValues('name')}</strong> to the{' '}
              <strong>{selectedRole?.title}</strong> position has been successfully submitted.
            </p>
            
             {schemaWarning && <Alert className="mb-6 bg-white border-gray-200">
                <AlertTriangle className="h-4 w-4 text-gray-600" />
                <AlertDescription className="text-gray-800">
                  Note: Due to a temporary database issue, the phone number may not have been saved, but your referral is complete.
                </AlertDescription>
              </Alert>}
            
            <div className="bg-blue-50 rounded-xl p-6 mb-8">
              <h3 className="font-bold text-blue-900 mb-2">What happens next?</h3>
              <div className="text-sm text-blue-700 space-y-2">
                <p>• HR will review the referral within 2-3 business days</p>
                <p>• You'll receive updates via email and in your dashboard</p>
                <p>• Potential bonus: <span className="font-bold">${selectedRole?.bonus_amount?.toLocaleString()}</span></p>
              </div>
            </div>

            <div className="space-y-4">
              <Button variant="outline" onClick={handleSubmitAnotherReferral} className="w-full rounded-lg">
                Submit Another Referral
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>;
  }
  return <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header with Progress */}
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center relative">
            <Button variant="ghost" size="sm" onClick={() => window.history.back()} className="absolute left-0 flex items-center gap-2 text-gray-600 hover:text-gray-900">
              <ArrowLeft size={20} />
              Back
            </Button>
            <h1 className="text-3xl font-bold text-gray-900">Role Hub</h1>
          </div>
          <p className="text-lg text-gray-600 mt-2">Find the perfect role and refer talented candidates to earn rewards</p>
          <div className="space-y-2">
            <Progress value={getStepProgress()} className="w-full max-w-md mx-auto" />
            <p className="text-sm text-gray-600">Step {currentStep} of 4</p>
          </div>

          {/* Four Steps Guide */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 max-w-5xl mx-auto mt-8">
            <div className="bg-white rounded-lg p-6 text-center shadow-sm border border-gray-200">
              <div className="text-lg font-semibold text-gray-900 mb-2">Step 1:</div>
              <p className="text-sm text-gray-600">Browse 'Open Roles' to view requirements</p>
            </div>
            <div className="bg-white rounded-lg p-6 text-center shadow-sm border border-gray-200">
              <div className="text-lg font-semibold text-gray-900 mb-2">Step 2:</div>
              <p className="text-sm text-gray-600">Click 'Refer Now' to upload candidate info</p>
            </div>
            <div className="bg-white rounded-lg p-6 text-center shadow-sm border border-gray-200">
              <div className="text-lg font-semibold text-gray-900 mb-2">Step 3:</div>
              <p className="text-sm text-gray-600">'Submit Candidate' to complete your referral</p>
            </div>
            <div className="bg-white rounded-lg p-6 text-center shadow-sm border border-gray-200">
              <div className="text-lg font-semibold text-gray-900 mb-2">Step 4:</div>
              <p className="text-sm text-gray-600">Why are they a great fit?</p>
            </div>
          </div>
          
          {/* Schema Warning */}
           {schemaWarning && <Alert className="max-w-2xl mx-auto bg-white border-gray-200">
               <AlertTriangle className="h-4 w-4 text-gray-600" />
               <AlertDescription className="text-gray-800">
                {schemaWarning}
              </AlertDescription>
            </Alert>}
        </div>

        {/* Step 1: Role Selection */}
        {currentStep === 1 && <Card className="rounded-xl shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="w-5 h-5" />
                Step 1: Select a Role
              </CardTitle>
              <p className="text-gray-600">Choose the position you'd like to refer someone for and click 'Select This Role'</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Enhanced Search and Filter Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                    <Input placeholder="Search roles..." className="rounded-lg pl-10" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                  </div>
                  
                  <div className="space-y-2">
                    <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                      <SelectTrigger className="rounded-lg">
                        <SelectValue placeholder="Filter by department" />
                      </SelectTrigger>
                      <SelectContent>
                        {departments.map(dept => <SelectItem key={dept} value={dept}>
                            {dept === 'all' ? 'All Departments' : dept}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Filter Actions */}
                <div className="flex items-center justify-between">
                  <Button onClick={handleClearFilters} variant="outline" size="sm" className="bg-slate-50 hover:bg-slate-100">
                    Clear Filters
                  </Button>
                  <div className="text-sm text-gray-600">
                    Showing {filteredRoles.length} role{filteredRoles.length !== 1 ? 's' : ''}
                    {(searchTerm || departmentFilter !== 'all') && ' (filtered)'}
                  </div>
                </div>
                
                {rolesLoading ? <div className="text-center py-8">
                    <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading roles...</p>
                  </div> : filteredRoles.length === 0 ? <div className="text-center py-8">
                    <p className="text-gray-600">No roles found matching your criteria.</p>
                    <Button onClick={handleClearFilters} variant="outline" size="sm" className="mt-2">
                      Clear filters to see all roles
                    </Button>
                  </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredRoles.map(role => <div key={role.id} onClick={() => handleRoleSelect(role)} className="border-2 border-gray-400 bg-white rounded-xl p-6 cursor-pointer hover:shadow-lg transition-all hover:border-gray-500 hover:bg-gray-50">
                        <div className="space-y-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h3 className="font-bold text-gray-900 text-lg mb-1">{role.title}</h3>
                              {role.Company && <p className="text-sm text-primary font-semibold mb-1">{role.Company}</p>}
                              <p className="text-sm text-gray-600 font-medium">{role.department}</p>
                            </div>
                            {role.priority === "high" && <Badge className="bg-red-100 text-red-800 border-red-200 text-xs">
                                High Priority
                              </Badge>}
                          </div>
                          
                          <div className="space-y-3">
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-gray-500" />
                              <span className="text-sm text-gray-700">{role.location}</span>
                            </div>
                            
                            {role.deadline && <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-gray-500" />
                                <span className="text-sm text-gray-700">
                                  Deadline: {new Date(role.deadline).toLocaleDateString()}
                                </span>
                              </div>}
                            
                            {role.salary_range && <div className="border-t pt-3">
                                <div className="flex justify-between items-center mb-2">
                                  <span className="text-sm text-gray-600">Salary Range</span>
                                  <span className="text-sm font-semibold text-gray-900">{role.salary_range}</span>
                                </div>
                              </div>}
                            
                            
                          </div>
                          
                          <Button className="w-full rounded-lg font-medium py-2">
                            Select This Role
                          </Button>
                        </div>
                      </div>)}
                  </div>}
              </div>
            </CardContent>
          </Card>}

        {/* Step 2: Job Description */}
        {currentStep === 2 && selectedRole && <Card className="rounded-xl shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Step 2: Job Description
              </CardTitle>
              <p className="text-gray-600">Review the role details before referring a candidate</p>
            </CardHeader>
            <CardContent className="space-y-8">
              {/* Role Header */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedRole.title}</h2>
                    {selectedRole.Company && <div className="flex items-center gap-2 mb-2">
                        <Building2 className="w-4 h-4 text-blue-600" />
                        <span className="text-blue-600 font-medium">{selectedRole.Company}</span>
                      </div>}
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Briefcase className="w-4 h-4" />
                        {selectedRole.department}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {selectedRole.location}
                      </span>
                      {selectedRole.deadline && <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          Deadline: {new Date(selectedRole.deadline).toLocaleDateString()}
                        </span>}
                    </div>
                  </div>
                  {selectedRole.priority === "high" && <Badge className="bg-red-100 text-red-800 border-red-200">
                      High Priority
                    </Badge>}
                </div>
                
                {selectedRole.salary_range && <div className="bg-white rounded-lg p-4 border">
                    <div className="flex items-center gap-2 mb-1">
                      <DollarSign className="w-4 h-4 text-green-600" />
                      <span className="font-medium text-gray-900">Salary Range</span>
                    </div>
                    <p className="text-lg font-bold text-gray-900">{selectedRole.salary_range}</p>
                  </div>}
              </div>

              {/* About This Role */}
              {selectedRole.description && <div className="space-y-4">
                  <h3 className="text-xl font-bold text-gray-900">About This Role</h3>
                  <div className="bg-gray-50 rounded-lg p-6">
                    <p className={`text-gray-700 leading-relaxed transition-all duration-300 ${isFullDescriptionExpanded ? '' : 'line-clamp-3'}`}>
                      {selectedRole.description}
                    </p>
                  </div>
                </div>}

              {/* Experience Section */}
              {selectedRole.experience && <div className="space-y-4">
                  <h3 className="text-xl font-bold text-gray-900">Experience</h3>
                  <div className="bg-orange-50 rounded-lg p-6 border border-orange-200">
                    <div className="whitespace-pre-wrap text-gray-700">
                      {selectedRole.experience}
                    </div>
                  </div>
                </div>}

              {/* Key Qualifications */}
              {selectedRole.requirements && <div className="space-y-4">
                  <h3 className="text-xl font-bold text-gray-900">Key Qualifications</h3>
                  <div className="bg-white rounded-lg border p-6">
                    <ul className="space-y-3">
                      {parseRequirements(selectedRole.requirements).map((requirement: string, index: number) => <li key={index} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{requirement}</span>
                        </li>)}
                    </ul>
                  </div>
                </div>}

              {/* Read More Button */}
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Job Details</h3>
                <button onClick={() => setIsFullDescriptionExpanded(!isFullDescriptionExpanded)} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 transition-colors">
                  <span>Read more</span>
                  {isFullDescriptionExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
              </div>

              {/* Collapsible Sections */}
              {isFullDescriptionExpanded && <div className="space-y-6 animate-fade-in">
                  {/* Requirements Section */}
                  {selectedRole.requirements && <div className="space-y-4">
                      <h3 className="text-xl font-bold text-gray-900">Requirements</h3>
                      <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                        <div className="space-y-3">
                          {parseRequirements(selectedRole.requirements).map((requirement: string, index: number) => <div key={index} className="flex items-start gap-3">
                              <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                              <span className="text-gray-700">{requirement}</span>
                            </div>)}
                        </div>
                      </div>
                    </div>}

                  {/* Required Tools Section */}
                  {(parseTools(selectedRole.required_tools).length > 0 || parseTools(selectedRole.Required_tools).length > 0) && <div className="space-y-4">
                      <h3 className="text-xl font-bold text-gray-900">Required Tools</h3>
                      <div className="bg-purple-50 rounded-lg p-6 border border-purple-200">
                        <div className="flex flex-wrap gap-2">
                          {parseTools(selectedRole.required_tools || selectedRole.Required_tools).map((tool: string, index: number) => <Badge key={index} className="bg-purple-100 text-purple-800 border-purple-200">
                              {tool}
                            </Badge>)}
                        </div>
                      </div>
                    </div>}

                  {/* Essential Skills Section */}
                  {selectedRole.Essential_Skills && <div className="space-y-4">
                      <h3 className="text-xl font-bold text-gray-900">Essential Skills</h3>
                      <div className="bg-green-50 rounded-lg p-6 border border-green-200">
                        <div className="whitespace-pre-wrap text-gray-700">
                          {selectedRole.Essential_Skills}
                        </div>
                      </div>
                    </div>}

                  {/* Why Refer Section */}
                  <div className="rounded-xl p-6 border border-green-200 bg-slate-50">
                    <h3 className="text-lg font-bold text-green-900 mb-3">Why refer for this role?</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-green-800">Competitive salary package</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-green-800">${selectedRole.bonus_amount?.toLocaleString()} referral bonus</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-green-800">Great career growth opportunity</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-green-800">Join a dynamic team</span>
                      </div>
                    </div>
                  </div>
                </div>}

              {/* Full Job Description Section */}
              {selectedRole.description}

              {/* Navigation */}
              <div className="flex justify-between pt-6">
                <Button variant="outline" onClick={() => setCurrentStep(1)} className="rounded-lg">
                  Back to Roles
                </Button>
                <Button onClick={() => setCurrentStep(3)} className="bg-blue-600 hover:bg-blue-700 rounded-lg">
                  Proceed to Refer Candidate
                </Button>
              </div>
            </CardContent>
          </Card>}

        {/* Step 3: Candidate Details */}
        {currentStep === 3 && selectedRole && <Form {...form}>
            <form onSubmit={form.handleSubmit(handleCandidateSubmit)} className="space-y-6">
              {/* Selected Role Summary */}
              <Card className="rounded-xl shadow-sm bg-blue-50 border-blue-200">
                <CardContent className="p-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="font-bold text-blue-900">{selectedRole.title}</h3>
                      <p className="text-blue-700">{selectedRole.department} • Bonus: ${selectedRole.bonus_amount?.toLocaleString()}</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setCurrentStep(2)} type="button">
                      View Details
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-xl shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Step 3: Candidate Information
                  </CardTitle>
                  <p className="text-gray-600">Provide details about the candidate you're referring</p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField control={form.control} name="name" render={({
                  field
                }) => <FormItem>
                          <FormLabel>Full Name *</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter candidate's full name" className="rounded-lg" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>} />
                    
                    <FormField control={form.control} name="email" render={({
                  field
                }) => <FormItem>
                          <FormLabel>Email Address *</FormLabel>
                          <FormControl>
                            <Input type="email" placeholder="candidate@email.com" className="rounded-lg" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>} />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField control={form.control} name="phone" render={({
                  field
                }) => <FormItem>
                          <FormLabel>
                            Phone Number {schemaValidated === false ? '(Optional)' : '*'}
                          </FormLabel>
                          <FormControl>
                            <Input type="tel" placeholder="Enter phone number" className="rounded-lg" {...field} />
                          </FormControl>
                          <p className="text-xs text-gray-500">
                            {schemaValidated === false ? "Phone number may not be saved due to technical issues" : "Contact number for the candidate"}
                          </p>
                          <FormMessage />
                        </FormItem>} />

                    <FormField control={form.control} name="linkedin" render={({
                  field
                }) => <FormItem>
                          <FormLabel>LinkedIn Profile *</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter LinkedIn profile or URL" className="rounded-lg" {...field} />
                          </FormControl>
                          <p className="text-xs text-gray-500">LinkedIn profile, username, or URL for professional background</p>
                          <FormMessage />
                        </FormItem>} />
                  </div>

                  <FormField control={form.control} name="relationship" render={({
                field
              }) => <FormItem>
                        <FormLabel>How do you know this candidate? *</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger className="rounded-lg">
                              <SelectValue placeholder="Select your relationship" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {relationships.map(relationship => <SelectItem key={relationship} value={relationship}>
                                {relationship}
                              </SelectItem>)}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>} />

                  <div className="space-y-2">
                    <Label>Resume/CV Upload</Label>
                    <FileUpload onFileUpload={handleFileUpload} acceptedTypes={['application/pdf', '.doc', '.docx']} maxSizeInMB={10} currentFile={form.getValues('resumeUrl')} />
                  </div>

                  <div className="flex justify-between">
                    <Button type="button" variant="outline" onClick={() => setCurrentStep(2)} className="rounded-lg bg-slate-50">
                      Back
                    </Button>
                    <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg">
                      Next: Add Details
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </form>
          </Form>}

        {/* Step 4: Additional Notes */}
        {currentStep === 4 && <Form {...form}>
            <form onSubmit={form.handleSubmit(handleFinalSubmit)} className="space-y-6">
              <Card className="rounded-xl shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5" />
                    Step 4: Why are they a great fit?
                  </CardTitle>
                  <p className="text-gray-600">Help HR understand why this candidate would excel in this role</p>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Error Alert */}
                  {(submissionError || referralError) && <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        {submissionError || referralError?.message}
                      </AlertDescription>
                    </Alert>}

                  <FormField control={form.control} name="notes" render={({
                field
              }) => <FormItem>
                        <FormLabel>Additional Notes for HR (Optional)</FormLabel>
                        <FormControl>
                          <Textarea placeholder="Share insights about their skills, experience, or why they'd be perfect for this role..." className="rounded-lg min-h-32" {...field} />
                        </FormControl>
                        <p className="text-xs text-gray-500">These details will help our recruiting team better evaluate the candidate</p>
                        <FormMessage />
                      </FormItem>} />

                  {/* Summary */}
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="font-bold text-gray-900 mb-4">Referral Summary</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Role:</span>
                        <span className="font-medium">{selectedRole?.title}</span>
                      </div>
                      
                      {/* Candidate Name */}
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Candidate:</span>
                        <div className="flex items-center gap-2">
                          {editingField === 'name' ? <>
                              <Input value={tempFormData.name || ''} onChange={e => setTempFormData({
                          ...tempFormData,
                          name: e.target.value
                        })} className="text-sm h-8" />
                              <Button size="sm" variant="ghost" onClick={() => {
                          form.setValue('name', tempFormData.name || '');
                          setEditingField(null);
                          setTempFormData({});
                        }}>
                                <Save className="w-3 h-3" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => {
                          setEditingField(null);
                          setTempFormData({});
                        }}>
                                <X className="w-3 h-3" />
                              </Button>
                            </> : <>
                              <span className="font-medium">{form.getValues('name')}</span>
                              <Button size="sm" variant="ghost" onClick={() => {
                          setEditingField('name');
                          setTempFormData({
                            name: form.getValues('name')
                          });
                        }}>
                                <Edit2 className="w-3 h-3" />
                              </Button>
                            </>}
                        </div>
                      </div>
                      
                      {/* Email */}
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Email:</span>
                        <div className="flex items-center gap-2">
                          {editingField === 'email' ? <>
                              <Input value={tempFormData.email || ''} onChange={e => setTempFormData({
                          ...tempFormData,
                          email: e.target.value
                        })} className="text-sm h-8" />
                              <Button size="sm" variant="ghost" onClick={() => {
                          form.setValue('email', tempFormData.email || '');
                          setEditingField(null);
                          setTempFormData({});
                        }}>
                                <Save className="w-3 h-3" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => {
                          setEditingField(null);
                          setTempFormData({});
                        }}>
                                <X className="w-3 h-3" />
                              </Button>
                            </> : <>
                              <span className="font-medium">{form.getValues('email')}</span>
                              <Button size="sm" variant="ghost" onClick={() => {
                          setEditingField('email');
                          setTempFormData({
                            email: form.getValues('email')
                          });
                        }}>
                                <Edit2 className="w-3 h-3" />
                              </Button>
                            </>}
                        </div>
                      </div>
                      
                      {/* Phone */}
                      {form.getValues('phone') && schemaValidated !== false && <div className="flex justify-between items-center">
                          <span className="text-gray-600">Phone:</span>
                          <div className="flex items-center gap-2">
                            {editingField === 'phone' ? <>
                                <Input value={tempFormData.phone || ''} onChange={e => setTempFormData({
                          ...tempFormData,
                          phone: e.target.value
                        })} className="text-sm h-8" />
                                <Button size="sm" variant="ghost" onClick={() => {
                          form.setValue('phone', tempFormData.phone || '');
                          setEditingField(null);
                          setTempFormData({});
                        }}>
                                  <Save className="w-3 h-3" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => {
                          setEditingField(null);
                          setTempFormData({});
                        }}>
                                  <X className="w-3 h-3" />
                                </Button>
                              </> : <>
                                <span className="font-medium">{form.getValues('phone')}</span>
                                <Button size="sm" variant="ghost" onClick={() => {
                          setEditingField('phone');
                          setTempFormData({
                            phone: form.getValues('phone')
                          });
                        }}>
                                  <Edit2 className="w-3 h-3" />
                                </Button>
                              </>}
                          </div>
                        </div>}
                      
                      {/* Relationship */}
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Relationship:</span>
                        <div className="flex items-center gap-2">
                          {editingField === 'relationship' ? <>
                              <Select value={tempFormData.relationship || ''} onValueChange={value => setTempFormData({
                          ...tempFormData,
                          relationship: value
                        })}>
                                <SelectTrigger className="text-sm h-8 w-40">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {relationships.map(rel => <SelectItem key={rel} value={rel}>{rel}</SelectItem>)}
                                </SelectContent>
                              </Select>
                              <Button size="sm" variant="ghost" onClick={() => {
                          form.setValue('relationship', tempFormData.relationship || '');
                          setEditingField(null);
                          setTempFormData({});
                        }}>
                                <Save className="w-3 h-3" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => {
                          setEditingField(null);
                          setTempFormData({});
                        }}>
                                <X className="w-3 h-3" />
                              </Button>
                            </> : <>
                              <span className="font-medium">{form.getValues('relationship')}</span>
                              <Button size="sm" variant="ghost" onClick={() => {
                          setEditingField('relationship');
                          setTempFormData({
                            relationship: form.getValues('relationship')
                          });
                        }}>
                                <Edit2 className="w-3 h-3" />
                              </Button>
                            </>}
                        </div>
                      </div>

                      {/* LinkedIn URL */}
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">LinkedIn:</span>
                        <div className="flex items-center gap-2">
                          {editingField === 'linkedin' ? <>
                              <Input value={tempFormData.linkedin || ''} onChange={e => setTempFormData({
                          ...tempFormData,
                          linkedin: e.target.value
                        })} className="text-sm h-8" />
                              <Button size="sm" variant="ghost" onClick={() => {
                          form.setValue('linkedin', tempFormData.linkedin || '');
                          setEditingField(null);
                          setTempFormData({});
                        }}>
                                <Save className="w-3 h-3" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => {
                          setEditingField(null);
                          setTempFormData({});
                        }}>
                                <X className="w-3 h-3" />
                              </Button>
                            </> : <>
                              <span className="font-medium">{form.getValues('linkedin')}</span>
                              <Button size="sm" variant="ghost" onClick={() => {
                          setEditingField('linkedin');
                          setTempFormData({
                            linkedin: form.getValues('linkedin')
                          });
                        }}>
                                <Edit2 className="w-3 h-3" />
                              </Button>
                            </>}
                        </div>
                      </div>
                      
                      
                    </div>
                  </div>

                  <div className="flex justify-between">
                    <Button type="button" variant="outline" onClick={() => setCurrentStep(3)} className="rounded-lg">
                      Back
                    </Button>
                    <div className="flex gap-2">
                      {submissionError && <Button type="button" variant="outline" onClick={handleRetrySubmission} disabled={isRetrying || isCreating} className="rounded-lg">
                          {isRetrying ? <>
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                              Retrying...
                            </> : 'Retry'}
                        </Button>}
                      <Button type="submit" disabled={isCreating || isRetrying} className="bg-green-600 hover:bg-green-700 rounded-lg">
                        {isCreating ? <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Submitting...
                          </> : 'Submit Referral'}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </form>
          </Form>}
      </div>
    </div>;
};
export default ReferralSubmissionFlow;